import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { useUserDirectory } from '../schedules/api';
import {
  useAppointmentsList,
  type AppointmentListFilter,
} from './api';
import { AppointmentsCalendar, type CalendarSlot } from './AppointmentsCalendar';
import { AppointmentModal, type ModalInitial } from './AppointmentModal';
import type { AppointmentItem } from '@crm/shared';

type ViewMode = 'day' | 'week';

function toDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeekISO(d: Date): string {
  // Tydzień zaczynamy w poniedziałek (zgodnie z polskim zwyczajem).
  const x = new Date(d);
  const day = x.getDay(); // 0 = ndz
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return toDateOnly(x);
}

function endOfWeekISO(d: Date): string {
  const start = startOfWeekISO(d);
  const x = new Date(`${start}T00:00:00`);
  x.setDate(x.getDate() + 6);
  return toDateOnly(x);
}

export function AppointmentsPage(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isEmployeeRole = user?.role === 'EMPLOYEE';

  const [view, setView] = useState<ViewMode>('day');
  const [date, setDate] = useState<Date>(() => new Date());
  // null = "wszyscy" (kolumny w day view); jeśli wybrano konkretnego — filtrujemy.
  const [filterEmployeeId, setFilterEmployeeId] = useState<number | null>(null);

  // Modal state.
  const [modalInitial, setModalInitial] = useState<ModalInitial | null>(null);

  const directoryQuery = useUserDirectory();
  const employees = useMemo(() => {
    const all = directoryQuery.data ?? [];
    return all.filter((u) => u.bookable && u.active);
  }, [directoryQuery.data]);

  // Dla EMPLOYEE: wymuszamy widok dnia + tylko jego kolumna.
  const effectiveEmployees = useMemo(() => {
    if (isEmployeeRole && user) {
      return employees.filter((e) => e.id === user.id);
    }
    if (filterEmployeeId !== null) {
      return employees.filter((e) => e.id === filterEmployeeId);
    }
    return employees;
  }, [isEmployeeRole, user, employees, filterEmployeeId]);

  const filter: AppointmentListFilter = useMemo(() => {
    if (view === 'day') {
      const day = toDateOnly(date);
      return {
        from: day,
        to: day,
        employeeId: isEmployeeRole && user ? user.id : filterEmployeeId ?? undefined,
      };
    }
    return {
      from: startOfWeekISO(date),
      to: endOfWeekISO(date),
      employeeId: isEmployeeRole && user ? user.id : filterEmployeeId ?? undefined,
    };
  }, [view, date, filterEmployeeId, isEmployeeRole, user]);

  const appointmentsQuery = useAppointmentsList(filter, employees.length > 0 || isEmployeeRole);

  const appointments = appointmentsQuery.data ?? [];

  function handleSlotClick(slot: CalendarSlot): void {
    setModalInitial({
      mode: 'create',
      startAt: slot.startAt,
      employeeId: slot.employeeId,
    });
  }

  function handleEventClick(item: AppointmentItem): void {
    setModalInitial({ mode: 'edit', appointment: item });
  }

  function shiftDate(deltaDays: number): void {
    const d = new Date(date);
    d.setDate(d.getDate() + deltaDays);
    setDate(d);
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900">{t('appointments.title')}</h1>
        <p className="text-sm text-zinc-500">{t('appointments.subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-3 shadow-sm">
        {!isEmployeeRole && (
          <div className="flex gap-1 rounded-md border border-zinc-200 p-1">
            <button
              type="button"
              onClick={() => setView('day')}
              className={`rounded px-3 py-1 text-sm transition ${
                view === 'day' ? 'bg-brand-600 text-white' : 'text-zinc-700 hover:bg-zinc-100'
              }`}
            >
              {t('appointments.view.day')}
            </button>
            <button
              type="button"
              onClick={() => setView('week')}
              className={`rounded px-3 py-1 text-sm transition ${
                view === 'week' ? 'bg-brand-600 text-white' : 'text-zinc-700 hover:bg-zinc-100'
              }`}
            >
              {t('appointments.view.week')}
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shiftDate(view === 'day' ? -1 : -7)}
            className="btn-secondary"
            aria-label="prev"
          >
            ‹
          </button>
          <button type="button" onClick={() => setDate(new Date())} className="btn-secondary">
            {t('appointments.today')}
          </button>
          <button
            type="button"
            onClick={() => shiftDate(view === 'day' ? 1 : 7)}
            className="btn-secondary"
            aria-label="next"
          >
            ›
          </button>
          <span className="ml-2 text-sm font-medium text-zinc-700">
            {view === 'day'
              ? new Intl.DateTimeFormat('pl-PL', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                }).format(date)
              : `${startOfWeekISO(date)} – ${endOfWeekISO(date)}`}
          </span>
        </div>

        {!isEmployeeRole && view === 'week' && (
          <select
            value={filterEmployeeId ?? ''}
            onChange={(e) =>
              setFilterEmployeeId(e.target.value === '' ? null : Number(e.target.value))
            }
            className="input max-w-xs"
          >
            <option value="">{t('appointments.allEmployees')}</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        )}

        {!isEmployeeRole && view === 'day' && (
          <select
            value={filterEmployeeId ?? ''}
            onChange={(e) =>
              setFilterEmployeeId(e.target.value === '' ? null : Number(e.target.value))
            }
            className="input max-w-xs"
          >
            <option value="">{t('appointments.allEmployees')}</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        )}

        <div className="ml-auto flex items-center gap-2">
          {appointmentsQuery.isFetching && (
            <span className="text-xs text-zinc-500">{t('common.loading')}</span>
          )}
          <button
            type="button"
            onClick={() =>
              setModalInitial({
                mode: 'create',
                startAt: null,
                employeeId: isEmployeeRole && user ? user.id : null,
              })
            }
            className="btn-primary"
          >
            {t('appointments.actions.add')}
          </button>
        </div>
      </div>

      {effectiveEmployees.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-zinc-500 shadow-sm">
          {t('appointments.noEmployees')}
        </div>
      ) : (
        <div className="rounded-lg bg-white shadow-sm">
          <AppointmentsCalendar
            view={view}
            date={date}
            employees={effectiveEmployees}
            appointments={appointments}
            onSlotClick={handleSlotClick}
            onEventClick={handleEventClick}
          />
        </div>
      )}

      {modalInitial && (
        <AppointmentModal
          initial={modalInitial}
          onClose={() => setModalInitial(null)}
          employees={employees}
        />
      )}
    </div>
  );
}
